import { Alert } from '@bbpos/ui';
import type { CurrencyCode } from '@bbpos/types';
import { GLASS, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import type { ImportReportStore, ProductImportReport } from '@/types/product';

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  gst_rate: 'GST rate',
  hsn_code: 'HSN code',
  barcode: 'Barcode',
  category: 'Category',
  unit: 'Unit',
  price: 'Price',
  cost: 'Cost',
  parent_sku: 'Parent SKU',
};

function storeName(s: ImportReportStore): string {
  const name = s.location_name ?? s.location_id;
  return s.location_code ? `${name} (${s.location_code})` : name;
}

function showValue(field: string, value: unknown, currency: CurrencyCode): string {
  if (value === null || value === undefined || value === '') return '—';
  if ((field === 'price' || field === 'cost') && typeof value === 'number') return formatPaise(value, currency);
  if (field === 'gst_rate') return `${String(value)}%`;
  return String(value);
}

function Stat({ label, value, tint, text }: { label: string; value: number; tint: string; text: string }) {
  return (
    <div className={cn('rounded-xl p-4 shadow-md transition-all duration-200 hover:shadow-lg animate-slide-up', tint)}>
      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <p className={cn('mt-1 text-2xl font-bold tabular-nums', text)}>{value.toLocaleString('en-IN')}</p>
    </div>
  );
}

/**
 * What a CSV import did — or, on a dry run, would do — to the master catalogue:
 * new and updated products, what each store gains, the stores the user may not
 * use, and the changes to existing products worth a look.
 */
export function ImportReportPanel({ report, currency }: { report: ProductImportReport; currency: CurrencyCode }) {
  return (
    <div className="space-y-4 animate-fade-in" data-testid="import-report">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat
          label="New products"
          value={report.new_masters}
          tint="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/10"
          text="text-teal-700 dark:text-teal-300"
        />
        <Stat
          label="Products updated"
          value={report.updated_masters}
          tint="bg-gradient-to-br from-brand-50 to-indigo-50 dark:from-brand-900/20 dark:to-indigo-900/10"
          text="text-indigo-700 dark:text-indigo-300"
        />
        <Stat
          label="Already up to date"
          value={report.unchanged_masters}
          tint="bg-white dark:bg-neutral-800 border border-neutral-100 dark:border-neutral-700"
          text="text-neutral-900 dark:text-white"
        />
      </div>

      {report.skipped && report.skipped.length > 0 && (
        <div className={cn(GLASS, 'overflow-hidden')} data-testid="import-skipped">
          <p className="px-4 pt-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">
            Skipped — SKU already exists ({report.skipped.length})
          </p>
          <p className="px-4 text-xs text-neutral-500 dark:text-neutral-400">
            {report.dry_run ? 'These rows will be left out' : 'These rows were left out'}. To change those products, tick
            “Update existing products” and import again.
          </p>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 dark:text-neutral-400">
                <th className="px-4 py-2 font-medium">Row</th>
                <th className="px-4 py-2 font-medium">SKU</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {report.skipped.map((s) => (
                <tr
                  key={s.row}
                  className="border-t border-neutral-100 transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 dark:border-neutral-700 dark:even:bg-neutral-800/40 dark:hover:bg-neutral-800"
                >
                  <td className="px-4 py-2 tabular-nums text-neutral-500">{s.row}</td>
                  <td className="px-4 py-2 tabular-nums text-neutral-700 dark:text-neutral-300">{s.sku}</td>
                  <td className="px-4 py-2">
                    <span className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                      SKU exists — skipped
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {report.refused_stores.length > 0 && (
        <Alert
          variant="warning"
          title={`You are not assigned to ${report.refused_stores.map(storeName).join(', ')}, so the import cannot add products there. Remove ${report.refused_stores.length === 1 ? 'it' : 'them'} from the stores, or ask an Owner.`}
        />
      )}

      {report.enables.length > 0 && (
        <div className={cn(GLASS, 'overflow-hidden')}>
          <p className="px-4 pt-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">Stores</p>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 dark:text-neutral-400">
                <th className="px-4 py-2 font-medium">Store</th>
                <th className="px-4 py-2 text-right font-medium">Newly added</th>
                <th className="px-4 py-2 text-right font-medium">Switched back on</th>
                <th className="px-4 py-2 text-right font-medium">Already sold</th>
              </tr>
            </thead>
            <tbody>
              {report.enables.map((e) => (
                <tr
                  key={e.location_id}
                  className="border-t border-neutral-100 transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 dark:border-neutral-700 dark:even:bg-neutral-800/40 dark:hover:bg-neutral-800"
                >
                  <td className="px-4 py-2 text-neutral-900 dark:text-white">{storeName(e)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-teal-700 dark:text-teal-300">{e.created}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-indigo-700 dark:text-indigo-300">{e.enabled}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-neutral-500 dark:text-neutral-400">{e.already}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {report.conflicts.length > 0 && (
        <div className={cn(GLASS, 'overflow-hidden')}>
          <p className="px-4 pt-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">
            Changes to existing products ({report.conflicts.length})
          </p>
          <p className="px-4 text-xs text-neutral-500 dark:text-neutral-400">
            A change that is not applied needs an Owner: only an Owner changes a product at every store.
          </p>
          <table className="mt-2 w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-neutral-500 dark:text-neutral-400">
                <th className="px-4 py-2 font-medium">Row</th>
                <th className="px-4 py-2 font-medium">SKU</th>
                <th className="px-4 py-2 font-medium">Field</th>
                <th className="px-4 py-2 font-medium">Now</th>
                <th className="px-4 py-2 font-medium">In the file</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {report.conflicts.map((c) => (
                <tr
                  key={`${c.row}-${c.field}`}
                  className="border-t border-neutral-100 transition-all duration-200 even:bg-neutral-50 hover:bg-brand-50 dark:border-neutral-700 dark:even:bg-neutral-800/40 dark:hover:bg-neutral-800"
                >
                  <td className="px-4 py-2 tabular-nums text-neutral-500">{c.row}</td>
                  <td className="px-4 py-2 tabular-nums text-neutral-700 dark:text-neutral-300">{c.sku}</td>
                  <td className="px-4 py-2 text-neutral-700 dark:text-neutral-300">{FIELD_LABELS[c.field] ?? c.field}</td>
                  <td className="px-4 py-2 text-neutral-500 dark:text-neutral-400">{showValue(c.field, c.current, currency)}</td>
                  <td className="px-4 py-2 font-medium text-neutral-900 dark:text-white">{showValue(c.field, c.incoming, currency)}</td>
                  <td className="px-4 py-2">
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold',
                        c.applied
                          ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                          : 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
                      )}
                    >
                      {c.applied ? (report.dry_run ? 'Will change' : 'Changed') : 'Not applied'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {report.opening_stock_skipped > 0 && (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Opening stock goes only on products the import adds to a store. {report.opening_stock_skipped} that a store
          already had {report.dry_run ? 'keep' : 'kept'} their stock.
        </p>
      )}
    </div>
  );
}
